import * as React from 'react'
import { ScrollView, useWindowDimensions, View } from 'react-native'
import { Card, Icon, List, Text } from 'react-native-paper'
import { createStyle } from '../../../../contexts/global-styles.provider'
import { useTranslation } from 'react-i18next'
import { TimeLineIcon } from '../../../../utils/meldd-icons'
import { UserLink } from '../../../../@ui/typo/user.link'
import Row from '../../../../@ui/containers/grid/row'
import { TouchableIconWithToolTip } from '../../../../utils/meldd-tooltip'
import { useSocketTimeline } from '../../../../socket/socket.hooks'
import { AppNavigation } from '../../../../navigation/types'
import { useNavigation } from '@react-navigation/native'
import { navigateToTimelinePayload } from '../../../../utils/target'
import { TimeLineAction, TimeLine } from '../../../../generated/graphql'
import {
  EagleViewShareCorner,
  type EagleViewShareParams,
} from './eagle-view.qrcode'

/**
 * Eagle view can be displayed in big screens
 */
const useFontSize = () => {
  const { width } = useWindowDimensions()
  if (width > 2560) return 28 // 4K displays and larger
  if (width > 1920) return 24 // Full HD TV
  if (width > 1280) return 20 // Large desktop/small TV
  if (width > 768) return 16 // Tablet/small desktop
  return 14 // Mobile phones
}
const PayloadDescription = ({ item }: { item: TimeLine }) => {
  const fontSize = useFontSize()

  const subtitleFontSize = fontSize - 2
  if (item.action === TimeLineAction.EditContributionCreated) {
    return (
      <Text
        variant="bodySmall"
        style={{ fontStyle: 'italic', fontSize: subtitleFontSize }}
      >
        "
        <Text style={{ fontSize: subtitleFontSize }}>
          {item.payload.originalText}
        </Text>
        "{' → '}"{item.payload.title}"
      </Text>
    )
  }
  return (
    <Text
      variant="bodySmall"
      style={{ fontStyle: 'italic', fontSize: subtitleFontSize }}
    >
      {item.payload.title}
    </Text>
  )
}
export default function EagleView({
  targetId,
  open,
  onClose,
  shareParams,
}: {
  targetId: string
  open: boolean
  onClose: () => void
  shareParams?: EagleViewShareParams
}) {
  const { t } = useTranslation(['eagle-view', 'common'])
  const fontSize = useFontSize()
  const styles = useStyle()
  const scrollViewRef = React.useRef<ScrollView>(null)
  const navigation = useNavigation<AppNavigation>()
  const timeline = useSocketTimeline(targetId)

  React.useEffect(() => {
    if (!open) return
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: false })
    }, 100)
  }, [open, timeline.length])

  if (!open) return null
  const opacity = (index: number) => {
    const opacity = [1, 0.7, 0.5, 0.35]
    const reverseIndex = timeline.length - 1 - index
    return opacity[reverseIndex] || 0.2
  }
  return (
    <Card style={styles.card}>
      <Card.Content style={styles.cardContent}>
        <Row justifyContent="flex-end">
          <TouchableIconWithToolTip
            tooltip={t('close')}
            icon={<Icon source="close" size={16} />}
            onPress={onClose}
            mode="text"
          />
        </Row>
        <View style={styles.scrollWrapper}>
          <ScrollView
            style={styles.scrollView}
            ref={scrollViewRef}
            contentContainerStyle={styles.scrollContent}
            horizontal={false}
          >
            {timeline.map((item, index) => (
              <List.Item
                key={index}
                title={
                  <Text
                    variant="labelSmall"
                    style={{ fontSize: fontSize, opacity: opacity(index) }}
                  >
                    {t(`action.${item.action}`, {
                      type: t(`action.${item.payload.type}`),
                    })}
                  </Text>
                }
                left={() => <TimeLineIcon item={item} size={fontSize} />}
                right={() =>
                  item.user ? (
                    <UserLink
                      user={item.user}
                      style={{ opacity: opacity(index) }}
                    />
                  ) : (
                    <Text
                      variant="labelSmall"
                      style={{ opacity: opacity(index) }}
                    >
                      {t('anonymous')}
                    </Text>
                  )
                }
                description={
                  <View style={{ opacity: opacity(index), paddingTop: 8 }}>
                    <PayloadDescription item={item} />
                  </View>
                }
                style={styles.listItem}
                onPress={() => {
                  navigateToTimelinePayload({
                    timeline: item,
                    navigation,
                  })
                }}
              />
            ))}
          </ScrollView>
          <EagleViewShareCorner shareParams={shareParams} />
        </View>
      </Card.Content>
    </Card>
  )
}

const useStyle = createStyle(() => ({
  card: {
    backgroundColor: 'transparent ', // instead of white with opacity
    margin: 4,
    maxHeight: '80%',
  },
  cardContent: {
    position: 'relative',
  },
  scrollWrapper: {
    position: 'relative',
    height: 150,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  listItem: {
    paddingBottom: 0,
    paddingTop: 0,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
  },
}))
